import { z } from "zod";

import { usageMetricsRange, usageMetricsRole, type UsageMetricsQuery } from "@kansai-ai-hub/schema";

/**
 * 利用状況ページ（/admin/usage）の URL search。
 *
 * 集計条件（range / role）と、一覧の表示条件（tab / q / status / sortBy / order / page）を
 * すべて URL に持つ。リロードしても消えず、絞り込んだ状態のまま共有できる。
 * 並び替えのキーは `sortBy`。他の一覧ルートの `sort` と同名にすると、ルート横断の
 * `navigate({ search: (prev) => ... })` の型（全ルートの search の和）と衝突する。
 * 旧ブックマークや手入力の不正値でページごと落ちないよう、各項目は `.catch()` で既定値へ寄せる。
 */
export const usageTabs = ["users", "organizations"] as const;
export type UsageTab = (typeof usageTabs)[number];

/**
 * - NEVER: 未ログイン（組織では「誰も利用していない」）
 * - NEVER_STALE: 発行から猶予日数以上たっても未ログイン（ユーザーのみ。要確認リストと同じ条件）
 * - DORMANT: 休眠（ユーザーのみ）
 * - ACTIVE: 当期間に利用あり
 */
export const usageStatusFilters = ["ALL", "NEVER", "NEVER_STALE", "DORMANT", "ACTIVE"] as const;
export type UsageStatusFilter = (typeof usageStatusFilters)[number];

export const usageSortFields = [
  "default",
  "name",
  "organization",
  "members",
  "lastActive",
  "activeDays",
  "actions",
  "approvedAt",
] as const;
export type UsageSortField = (typeof usageSortFields)[number];

export const USAGE_PAGE_SIZE = 20;
/** 検索語の上限文字数。入力欄の maxLength と揃え、超過で URL 側が "" に丸められて入力欄が消えるのを防ぐ。 */
export const USAGE_SEARCH_MAX_LENGTH = 100;

export const usageSearchSchema = z.object({
  range: usageMetricsRange.catch("28d").default("28d"),
  tab: z.enum(usageTabs).catch("users").default("users"),
  q: z.string().max(USAGE_SEARCH_MAX_LENGTH).catch("").default(""),
  /** 集計対象のロール（セクション最上部のタブ）。一覧だけでなく全指標に効く。 */
  role: usageMetricsRole.catch("ALL").default("ALL"),
  status: z.enum(usageStatusFilters).catch("ALL").default("ALL"),
  sortBy: z.enum(usageSortFields).catch("default").default("default"),
  order: z.enum(["asc", "desc"]).catch("asc").default("asc"),
  page: z.coerce.number().int().min(1).catch(1).default(1),
});
export type UsageSearch = z.infer<typeof usageSearchSchema>;

/** API に渡す集計条件だけを取り出す（表示条件を query key に混ぜない）。 */
export function toUsageMetricsQuery(
  search: Pick<UsageSearch, "range" | "role">,
): UsageMetricsQuery {
  return { range: search.range, role: search.role };
}
